import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { useAdminAuth } from "@/lib/auth/admin-auth-context";
import { describeAdminError } from "./adminErrors";

/**
 * Formulaire de connexion du back-office admin : email + mot de passe (pas
 * d'OTP, contrairement à la connexion client). Porté depuis
 * apps/web/components/admin/AdminLoginForm.tsx — seul changement de fond :
 * `adminLoginAction` (Server Action posant des cookies httpOnly côté Next.js)
 * remplacé par `useAdminAuth().login()` (appelle directement `POST
 * /admin/auth/login`, cookie httpOnly posé par apps/api lui-même) ; `useRouter().push`
 * → `useNavigate()`. Pas de `router.refresh()` : pas de cache serveur à invalider
 * dans ce SPA.
 */
export function AdminLoginForm() {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [isPending, setIsPending] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("Entrez votre email et votre mot de passe.");
      return;
    }

    setIsPending(true);
    try {
      await login(trimmedEmail, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(describeAdminError(err));
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm">
      <h1 className="font-display text-2xl font-bold text-navy">Connexion admin</h1>
      <p className="mt-2 text-sm text-muted">
        Accès réservé à l&apos;équipe VVS pour la gestion du back-office.
      </p>

      <form className="mt-6 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <div className="flex flex-col gap-1">
          <label htmlFor="admin-login-email" className="text-xs font-medium uppercase tracking-wide text-navy">
            Email
          </label>
          <input
            id="admin-login-email"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            placeholder="admin@vvs.ci"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="rounded-sm border border-line bg-white px-3 py-3 text-sm text-ink focus:border-gold focus:outline-none"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "admin-login-error" : undefined}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="admin-login-password" className="text-xs font-medium uppercase tracking-wide text-navy">
            Mot de passe
          </label>
          <input
            id="admin-login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="rounded-sm border border-line bg-white px-3 py-3 text-sm text-ink focus:border-gold focus:outline-none"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "admin-login-error" : undefined}
          />
        </div>

        {error ? (
          <p id="admin-login-error" role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={isPending} className="w-full">
          {isPending ? "Connexion en cours…" : "Se connecter"}
        </Button>
      </form>
    </div>
  );
}
